import React, { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import validateInput from "~utils/validate_input/validate_input";
import useDebounce from "~hooks/use_debounce";
import {
  borderStyle,
  errorStyle,
  hasLabelStyle,
  helperTextStyle,
  inputStyle,
  isFocusedStyle,
  labelStyle,
  sizeLgStyle,
  sizeMdStyle,
  sizeSmStyle,
  wrapperStyle,
} from "./input.module.scss";
import InputClearButton from "./input_components/input_clear_button/input_clear_button";
import InputLeadingIcon from "./input_components/input_leading_icon/input_leading_icon";
import InputTrailingIcon from "./input_components/input_trailing_icon/input_trailing_icon";

export default function Input({
  autoComplete,
  hasBorder,
  helperText,
  inputKey,
  isRequired,
  label,
  leadingIcon,
  onChange,
  onFocus,
  placeholder,
  trailingIcon,
  type,
  validationRegex,
  value,
  allowClear,
  size,
}) {
  const [localFocusState, setLocalFocusState] = useState(value);
  const [inputValue, setInputValue] = useState(value);
  const [errorState, setErrorState] = useState(false);

  const inputClassNames = classNames(inputStyle, {
    [isFocusedStyle]: localFocusState,
    [errorStyle]: errorState,
    [hasLabelStyle]: label,
    [borderStyle]: hasBorder,
    [sizeSmStyle]: size === "sm",
    [sizeMdStyle]: size === "md",
    [sizeLgStyle]: size === "lg",
  });

  const wrapperClassNames = classNames(wrapperStyle, {
    [hasLabelStyle]: label,
  });

  const debouncedInputValue = useDebounce({ value: inputValue, delay: 300 });

  const handleClearInput = useCallback(() => {
    setInputValue("");
  }, []);

  const handleLocalFocus = useCallback(() => {
    setLocalFocusState(true);
    if (onFocus) {
      onFocus();
    }
  }, [onFocus]);

  useEffect(() => {
    if (!value) {
      handleClearInput();
    }
  }, [handleClearInput, value]);

  const handleLocalBlur = useCallback(() => {
    setLocalFocusState(false);
    if (onFocus) {
      onFocus();
    }
  }, [onFocus]);

  useEffect(() => {}, [debouncedInputValue, validationRegex]);

  useEffect(() => {
    onChange(debouncedInputValue);
    const isInputValid = validateInput({
      value: debouncedInputValue,
      validationRegex,
    });
    if (debouncedInputValue && !isInputValid) {
      setErrorState(true);
    }
    if (isInputValid) {
      setErrorState(false);
    }
  }, [debouncedInputValue, onChange, validationRegex]);

  const shouldShowClearButton = allowClear && inputValue.length > 0;

  return (
    <div className={wrapperClassNames}>
      <label className={labelStyle} htmlFor={inputKey}>
        {label && (
          <>
            {label}
            {isRequired && "*"}
          </>
        )}
        <div role={type} className={inputClassNames}>
          {leadingIcon && <InputLeadingIcon icon={leadingIcon} size={20} />}
          <input
            autoComplete={autoComplete}
            id={inputKey}
            name={inputKey}
            onChange={(event) => {
              setInputValue(event.target.value);
            }}
            onFocus={handleLocalFocus}
            onBlur={handleLocalBlur}
            placeholder={placeholder}
            type={type}
            value={inputValue}
          />
          {trailingIcon && !shouldShowClearButton && (
            <InputTrailingIcon icon={trailingIcon} iconSize={20} />
          )}
          {shouldShowClearButton && (
            <InputClearButton iconSize={20} onClick={handleClearInput} />
          )}
        </div>
        {errorState && helperText && (
          <div className={helperTextStyle}> {helperText}</div>
        )}
      </label>
    </div>
  );
}

Input.propTypes = {
  allowClear: PropTypes.bool,
  autoComplete: PropTypes.string,
  hasBorder: PropTypes.bool,
  helperText: PropTypes.string,
  inputKey: PropTypes.string,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  leadingIcon: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  placeholder: PropTypes.string,
  trailingIcon: PropTypes.string,
  type: PropTypes.string,
  validationRegex: PropTypes.instanceOf(RegExp),
  value: PropTypes.string,
  size: PropTypes.string,
};

Input.defaultProps = {
  allowClear: false,
  autoComplete: "on",
  hasBorder: true,
  helperText: null,
  inputKey: null,
  isRequired: false,
  label: null,
  leadingIcon: null,
  onChange: () => {},
  onFocus: () => {},
  placeholder: "",
  trailingIcon: null,
  type: "text",
  validationRegex: null,
  value: "",
  size: "md",
};
